import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskAPI, ProjectAPI } from '../lib/api';
import { Input, Textarea } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { CronBuilder } from '../components/CronBuilder';
import { TopBar } from '../components/TopBar';
import { useToast } from '../components/ui/Toast';
import { ArrowLeft } from 'lucide-react';

const TASK_TYPES = [
  { value: 'SHELL', label: 'Shell' },
  { value: 'HTTP', label: 'HTTP' },
  { value: 'BUILTIN', label: 'Builtin' },
];

const HTTP_METHODS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'DELETE', label: 'DELETE' },
];

const BUILTIN_KEYS = [
  { value: 'renew_cert', label: 'renew_cert' },
  { value: 'restart_service', label: 'restart_service' },
  { value: 'cleanup_dir', label: 'cleanup_dir' },
  { value: 'ping_sitemap', label: 'ping_sitemap' },
  { value: 'tg_alert', label: 'tg_alert' },
  { value: 'disk_alert', label: 'disk_alert' },
  { value: 'ssl_expiry_alert', label: 'ssl_expiry_alert' },
  { value: 'check_nginx', label: 'check_nginx' },
];

interface TaskFormData {
  name: string;
  description: string;
  type: string;
  schedule: string;
  command: string;
  workdir: string;
  user: string;
  method: string;
  url: string;
  headers: string;
  body: string;
  builtinKey: string;
  builtinArgs: string;
  timeoutSec: string;
  ownerProject: string;
  notifyOnFail: boolean;
}

interface TaskFormProps {
  initialData?: any;
  taskId?: number;
  onSuccess?: () => void;
}

export function TaskForm({ initialData, taskId, onSuccess }: TaskFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<TaskFormData>({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    type: initialData?.type ?? 'SHELL',
    schedule: initialData?.schedule ?? '0 * * * *',
    command: initialData?.command ?? '',
    workdir: initialData?.workdir ?? '',
    user: initialData?.user ?? '',
    method: initialData?.method ?? 'GET',
    url: initialData?.url ?? '',
    headers: initialData?.headers ? JSON.stringify(initialData.headers, null, 2) : '',
    body: initialData?.body ?? '',
    builtinKey: initialData?.builtinKey ?? 'renew_cert',
    builtinArgs: initialData?.args ? JSON.stringify(initialData.args, null, 2) : '',
    timeoutSec: String(initialData?.timeoutSec ?? ''),
    ownerProject: initialData?.ownerProject ?? '',
    notifyOnFail: initialData?.notifyOnFail ?? false,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => ProjectAPI.list(),
  });
  const projects: any[] = projectsData?.items ?? projectsData ?? [];

  const projectOptions = [
    { value: '', label: 'Без проєкту' },
    ...projects.map((p: any) => ({ value: p.slug || p.id, label: p.name })),
  ];

  function set(key: keyof TaskFormData, val: any) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: any = {
        name: form.name,
        description: form.description || undefined,
        type: form.type,
        schedule: form.schedule,
        timeoutSec: form.timeoutSec ? Number(form.timeoutSec) : undefined,
        ownerProject: form.ownerProject || undefined,
        notifyOnFail: form.notifyOnFail,
      };

      if (form.type === 'SHELL') {
        payload.command = form.command;
        payload.workdir = form.workdir || undefined;
        payload.user = form.user || undefined;
      } else if (form.type === 'HTTP') {
        payload.method = form.method;
        payload.url = form.url;
        try { payload.headers = form.headers ? JSON.parse(form.headers) : undefined; } catch {}
        payload.body = form.body || undefined;
      } else if (form.type === 'BUILTIN') {
        payload.builtinKey = form.builtinKey;
        try { payload.args = form.builtinArgs ? JSON.parse(form.builtinArgs) : undefined; } catch {}
      }

      if (taskId) return TaskAPI.update(taskId, payload);
      return TaskAPI.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate({ to: '/tasks' });
      }
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }}
      className="space-y-4 max-w-2xl"
    >
      <Input
        label="Назва *"
        value={form.name}
        onChange={(e) => set('name', e.target.value)}
        required
        placeholder="my-task"
      />
      <Textarea
        label="Опис"
        value={form.description}
        onChange={(e) => set('description', e.target.value)}
        placeholder="Опис задачі..."
        rows={2}
      />
      <Select
        label="Тип"
        options={TASK_TYPES}
        value={form.type}
        onChange={(e) => set('type', e.target.value)}
      />
      <div>
        <label className="text-sm text-tg-text-secondary block mb-1">Розклад</label>
        <CronBuilder value={form.schedule} onChange={(v) => set('schedule', v)} />
      </div>

      {form.type === 'SHELL' && (
        <>
          <Textarea
            label="Команда *"
            value={form.command}
            onChange={(e) => set('command', e.target.value)}
            required
            placeholder="/bin/bash -c 'echo hello'"
            className="font-mono text-xs"
            rows={3}
          />
          <Input
            label="Робоча директорія"
            value={form.workdir}
            onChange={(e) => set('workdir', e.target.value)}
            placeholder="/opt/myapp"
          />
          <Input
            label="Користувач (необов'язково)"
            value={form.user}
            onChange={(e) => set('user', e.target.value)}
            placeholder="www-data"
          />
        </>
      )}

      {form.type === 'HTTP' && (
        <>
          <Select
            label="Метод"
            options={HTTP_METHODS}
            value={form.method}
            onChange={(e) => set('method', e.target.value)}
          />
          <Input
            label="URL *"
            value={form.url}
            onChange={(e) => set('url', e.target.value)}
            required
            placeholder="https://example.com/webhook"
            type="url"
          />
          <Textarea
            label="Заголовки (JSON)"
            value={form.headers}
            onChange={(e) => set('headers', e.target.value)}
            placeholder='{"Authorization": "Bearer token"}'
            className="font-mono text-xs"
            rows={3}
          />
          <Textarea
            label="Body"
            value={form.body}
            onChange={(e) => set('body', e.target.value)}
            placeholder='{"key": "value"}'
            className="font-mono text-xs"
            rows={3}
          />
        </>
      )}

      {form.type === 'BUILTIN' && (
        <>
          <Select
            label="Builtin"
            options={BUILTIN_KEYS}
            value={form.builtinKey}
            onChange={(e) => set('builtinKey', e.target.value)}
          />
          <Textarea
            label="Аргументи (JSON)"
            value={form.builtinArgs}
            onChange={(e) => set('builtinArgs', e.target.value)}
            placeholder='{"domain": "example.com"}'
            className="font-mono text-xs"
            rows={3}
          />
        </>
      )}

      <Input
        label="Timeout (секунди)"
        type="number"
        value={form.timeoutSec}
        onChange={(e) => set('timeoutSec', e.target.value)}
        placeholder="60"
      />
      <Select
        label="Проєкт"
        options={projectOptions}
        value={form.ownerProject}
        onChange={(e) => set('ownerProject', e.target.value)}
      />
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="notifyOnFail"
          checked={form.notifyOnFail}
          onChange={(e) => set('notifyOnFail', e.target.checked)}
          className="w-4 h-4"
        />
        <label htmlFor="notifyOnFail" className="text-sm text-tg-text">
          Сповіщати при помилці
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" loading={saveMut.isPending}>
          {taskId ? 'Зберегти' : 'Створити задачу'}
        </Button>
        {!taskId && (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: '/tasks' })}
          >
            Скасувати
          </Button>
        )}
      </div>
    </form>
  );
}

export function NewTaskPage() {
  const navigate = useNavigate();
  return (
    <div>
      <TopBar
        title="Нова задача"
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/tasks' })}>
            <ArrowLeft size={14} /> Назад
          </Button>
        }
      />
      <div className="p-6">
        <TaskForm />
      </div>
    </div>
  );
}
